import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Settings, Loader2, Mail, CheckCircle } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const RecipientAuth = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [email, setEmail] = useState("");
  const [magicLinkSent, setMagicLinkSent] = useState(false);

  useEffect(() => {
    const token = searchParams.get('token');
    if (token) {
      verifyToken(token);
    }
  }, [searchParams]);

  const verifyToken = async (token) => {
    setVerifying(true);
    try {
      await axios.get(`${API}/auth/recipient/verify?token=${token}`);
      localStorage.setItem('recipient_token', token);
      toast.success("Access granted!");
      navigate("/recipient/wallet");
    } catch (err) {
      toast.error("Invalid or expired link");
    } finally {
      setVerifying(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await axios.post(`${API}/auth/recipient/magic-link`, { email });
      setMagicLinkSent(true);
      toast.success("Magic link sent! Check console for demo.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send magic link");
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" data-testid="recipient-verifying">
        <Loader2 className="h-8 w-8 animate-spin text-slate-900" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 flex items-center justify-center px-6" data-testid="recipient-auth-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Settings className="h-10 w-10 text-slate-900" />
            <span className="text-2xl font-semibold text-slate-900" style={{fontFamily: 'Manrope'}}>Certificate Machine</span>
          </div>
          <h1 className="text-3xl font-medium text-slate-900 mb-2" style={{fontFamily: 'Playfair Display'}}>View Your Credentials</h1>
          <p className="text-slate-600">Enter your email to access your certificate wallet</p>
        </div>

        {magicLinkSent ? (
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-8 text-center" data-testid="magic-link-sent">
            <CheckCircle className="h-16 w-16 text-green-600 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-900 mb-2" style={{fontFamily: 'Playfair Display'}}>Check Your Email</h2>
            <p className="text-slate-600 mb-4">We've sent a magic link to <strong>{email}</strong></p>
            <p className="text-sm text-slate-500 mb-6">Click the link in the email to access your credentials</p>
            <Button
              onClick={() => setMagicLinkSent(false)}
              variant="outline"
              className="bg-white text-slate-900 border border-slate-200 hover:bg-slate-50 rounded-lg px-6 py-2"
              data-testid="try-different-email-btn"
            >
              Try a different email
            </Button>
          </div>
        ) : (
          <div className="bg-white border border-slate-200 rounded-xl shadow-sm p-8" data-testid="recipient-auth-form">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="email" className="text-sm font-medium text-slate-700 mb-1.5 block">Email Address</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  className="h-11 rounded-lg border-slate-200"
                  data-testid="email-input"
                />
                <p className="text-xs text-slate-500 mt-1">We'll send you a secure link to access your credentials</p>
              </div>

              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-slate-900 text-white hover:bg-slate-800 rounded-lg py-3 font-medium"
                data-testid="send-magic-link-btn"
              >
                {loading ? (
                  <><Loader2 className="h-4 w-4 animate-spin mr-2" /> Sending...</>
                ) : (
                  <><Mail className="h-4 w-4 mr-2" /> Send Magic Link</>
                )}
              </Button>
            </form>
          </div>
        )}

        <div className="mt-6 text-center">
          <button onClick={() => navigate("/")} className="text-sm text-slate-500 hover:text-slate-700" data-testid="back-home-link">
            ← Back to home
          </button>
        </div>
      </div>
    </div>
  );
};

export default RecipientAuth;
